import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_TIME_FORMAT,
  ISODateTimeType,
  Timestamp,
  TypeGuard,
  toTimestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { isNumber, isString, kebabCase } from 'lodash';
import * as moment from 'moment-timezone';
import {
  STERILISATION_CYCLE_DESTINATION_ENTITY,
  STERILISATION_CYCLE_RESOURCE_TYPE,
} from '../../../destination/entities/sterilisation-cycle';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const STERILISATION_CYCLE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Sterilisation Cycle List',
    description: '',
    idPrefix: STERILISATION_CYCLE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface ICorePracticeSterilisationCycle {
  cycleNumber: string; // "10388"
  steriliser: string; // "Lisa 1"
  runDateUtc: ISODateTimeType; // 2023-06-13 05:52:00.0000000
  providerId?: number;
}

export function isCorePracticeSterilisationCycle(
  item: unknown,
  logError: boolean
): item is ICorePracticeSterilisationCycle {
  return TypeGuard.interface<ICorePracticeSterilisationCycle>(
    {
      cycleNumber: isString,
      steriliser: isString,
      runDateUtc: isString,
      providerId: TypeGuard.nilOr(isNumber),
    },
    logError
  )(item);
}

export interface ICorePracticeSterilisationCycleTranslations {
  runDate: Timestamp;
}

/**
 * Identifies a Core Practice sterilisation cycle. Shared by the cycle source
 * and the sterilisation records so both resolve the same cycle.
 */
export function getSterilisationCycleKey(
  steriliser: string,
  cycleNumber: string,
  runDateUtc: ISODateTimeType
): string {
  const normalisedCycleNumber = cycleNumber.trim().replace(/^0+(?=.)/, '');
  const runDate = moment
    .utc(runDateUtc, ISO_DATE_TIME_FORMAT)
    .format('YYYYMMDDHHmmss');
  return [kebabCase(steriliser), normalisedCycleNumber, runDate].join('-');
}

/**
 * Core Practice has no cycle table. Barcodes can identify a pack rather than
 * a run, and cycle numbers reset, so a cycle is each distinct steriliser,
 * cycle number and run date. The treatment provider becomes the staffer only
 * when every treatment in the cycle shares one provider.
 */
const STERILISATION_CYCLE_SOURCE_QUERY = `
SELECT DISTINCT ON (
  steri.Steriliser,
  ltrim(convert_to_text(steri.CycleNo), '0'),
  steri.CycleDateUtc
)
  convert_to_text(steri.CycleNo) AS cycle_number,
  steri.Steriliser AS steriliser,
  steri.CycleDateUtc AS run_date_utc,
  CASE
    WHEN MIN(treatment.ProviderId) OVER cycle_rows = MAX(treatment.ProviderId) OVER cycle_rows
    THEN treatment.ProviderId
  END AS provider_id
FROM tblSteriTracking AS steri
INNER JOIN tblTreatment AS treatment
  ON treatment.TreatmentId = convert_to_integer(steri.TreatmentId)
WHERE
  convert_to_text(steri.CycleNo) <> ''
  AND steri.CycleDateUtc <> ''
  AND steri.Steriliser <> ''
WINDOW cycle_rows AS (
  PARTITION BY
    steri.Steriliser,
    ltrim(convert_to_text(steri.CycleNo), '0'),
    steri.CycleDateUtc
)
ORDER BY
  steri.Steriliser,
  ltrim(convert_to_text(steri.CycleNo), '0'),
  steri.CycleDateUtc,
  steri.SteriTrackingId
`;

export type CorePracticeSterilisationCycle = ISourceEntityTypes<
  ICorePracticeSterilisationCycle,
  ICorePracticeSterilisationCycleTranslations
>;

export class SterilisationCycleSourceEntity extends BaseSourceEntity<CorePracticeSterilisationCycle> {
  sourceEntity = STERILISATION_CYCLE_SOURCE_ENTITY;
  entityResourceType = STERILISATION_CYCLE_RESOURCE_TYPE;
  sourceQuery = STERILISATION_CYCLE_SOURCE_QUERY;
  migrationDestinations = [STERILISATION_CYCLE_DESTINATION_ENTITY.metadata.key];
  verifySourceFn = isCorePracticeSterilisationCycle;

  translate(
    data: ICorePracticeSterilisationCycle,
    _timezone: Timezone
  ): ICorePracticeSterilisationCycleTranslations {
    return {
      runDate: toTimestamp(moment.utc(data.runDateUtc, ISO_DATE_TIME_FORMAT)),
    };
  }

  getSourceRecordId(data: ICorePracticeSterilisationCycle): string {
    return getSterilisationCycleKey(
      data.steriliser,
      data.cycleNumber,
      data.runDateUtc
    );
  }

  getSourceLabel(data: ICorePracticeSterilisationCycle): string {
    return this.getSourceRecordId(data);
  }

  getFilterData(): object {
    return {};
  }
}
